import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Package, RefreshCw, ListOrdered, ArrowRightLeft } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading } from '../components/gtpl/GtplKit';

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

// Spare ("balance") boxes GTPL shows for each of our entities, read from Saathi's
// Reports → Entity Wise STB Distribution Information. The page shows the last saved reading;
// Refresh reads the report live (a few seconds). Read-only — nothing is changed on GTPL.
export default function GtplBoxStockPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canView = hasModuleRight('gtplSaathi', 'boxStock');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getBoxStock();
      setRows(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load box stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const { data } = await gtplSaathiAPI.refreshBoxStock();
      setRows(data.data || []);
      toast.success('Box stock updated from GTPL Saathi');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to read box stock';
      setError(msg);
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const totalBalance = rows.reduce((n, r) => n + (r.totalBalance || 0), 0);
  const hasData = rows.some((r) => r.fetchedAt);
  const lastFetched = rows.map((r) => r.fetchedAt).filter(Boolean).sort().pop();

  return (
    <Screen>
      <PageHead icon={Package} tone="adv" title="Box Stock"
        sub={'Spare boxes GTPL has issued to each entity that aren\'t on any subscriber — the "balance" in GTPL Saathi\'s Entity Wise STB Distribution report. Read-only.'}>
        <button type="button" className="btn btn-sm btn-primary" disabled={refreshing || !canView} onClick={handleRefresh}>
          <RefreshCw size={14} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
          {refreshing ? 'Reading GTPL…' : 'Refresh from GTPL'}
        </button>
      </PageHead>

      {!canView && <Notice tone="bad">You don't have permission to view box stock.</Notice>}
      {error && (
        <Notice tone="bad"
          actions={/session expired/i.test(error) ? <Link to="/gtpl-saathi-settings" style={{ fontWeight: 600 }}>Paste a fresh cookie →</Link> : null}>
          {error}
        </Notice>
      )}

      <Panel icon={Package} tone="adv" title="Spare boxes by entity" sub={lastFetched ? `Last read ${fmtDateTime(lastFetched)}` : undefined}>
        {loading ? (
          <Loading />
        ) : !hasData ? (
          <Empty icon={Package} title="Nothing saved yet">Press "Refresh from GTPL".</Empty>
        ) : (
          <>
            <Stats>
              <Stat tone={totalBalance ? 'ok' : 'bad'} value={totalBalance} label="Spare boxes (all entities)" />
            </Stats>
            <div className="gx-list" style={{ marginTop: 14 }}>
              {rows.map((r) => (
                <Item key={r.entityCode} tone={r.totalBalance ? 'ok' : undefined}
                  title={r.entityCode} sub={r.networkName || ''}
                  right={<Pill tone={r.totalBalance ? 'ok' : 'mute'} dot>{r.totalBalance ?? '—'} spare</Pill>}
                  meta={r.fetchedAt ? <>
                    <span>Spare: SD {r.sdBalance} · HD {r.hdBalance}</span>
                    <span>Active {r.totalActive ?? '—'}: SD {r.sdActive} · HD {r.hdActive}</span>
                    <span>Inactive {r.totalInactive ?? '—'}: SD {r.sdInactive} · HD {r.hdInactive}</span>
                    <span>Total {r.totalStb ?? '—'}: SD {r.totalSd} · HD {r.totalHd}</span>
                  </> : <span>Not read yet</span>} />
              ))}
            </div>
          </>
        )}
      </Panel>

      {canView && <SpareSerials onSnapshot={setRows} />}
      {canView && <EntityCheck />}
    </Screen>
  );
}

// Active CATV customers whose entity breaks the owner's rules: regular auto-renew belong in
// SPBP0042, CableTV + Broadband combos in SPBP0368 (DTD and AMP aren't checked). Each one needs
// the transfer process on GTPL. CRM data only; a customer's entity is refreshed by the GTPL check.
function EntityCheck() {
  const [data, setData] = useState(null);
  useEffect(() => {
    gtplSaathiAPI.getEntityCheck().then((r) => setData(r.data.data)).catch((err) => toast.error(err.response?.data?.message || 'Entity check failed'));
  }, []);
  if (!data) return null;
  return (
    <Panel icon={ArrowRightLeft} tone={data.wrong.length ? 'warn' : 'ok'} title="Entity check" count={`${data.wrong.length} to transfer`}
      sub={`Regular auto-renew customers belong in SPBP0042 and CableTV + Broadband customers in SPBP0368.${data.unknown > 0 ? ` ${data.unknown} more have no entity saved in the CRM yet, so they can't be checked.` : ''}`}>
      {data.wrong.length === 0 ? (
        <Empty tone="ok" title="Every checked customer is in the right entity" />
      ) : (
        <div className="gx-list">
          {data.wrong.map((w) => (
            <Item key={w._id} tone="warn"
              title={<Link to={`/customers/${w._id}`}>{w.name}</Link>}
              sub={<>{w.customerId} · <span style={{ fontFamily: 'monospace' }}>{w.accountNumber}</span></>}
              right={<><Pill tone="mute">{w.current}</Pill> → <Pill tone="ok">{w.expected}</Pill></>}
              meta={<span>{w.reason}</span>} />
          ))}
        </div>
      )}
    </Panel>
  );
}

// Serial numbers of the blank boxes, read from GTPL's STB INFO report (Reports → 2. STB INFO →
// STB Details, status BALANCE). Slow (about a minute per entity with spares), so the server reads
// it in the background and this polls.
function SpareSerials({ onSnapshot }) {
  const [state, setState] = useState({ status: 'idle' });
  const timer = useRef(null);

  const poll = useCallback(async () => {
    clearTimeout(timer.current);
    try {
      const { data } = await gtplSaathiAPI.getBoxSerials();
      setState(data.data);
      if (data.data.status === 'running') timer.current = setTimeout(poll, 4000);
      else if (data.data.status === 'done') {
        // The read re-reads Box Stock first; show those fresh counts too.
        gtplSaathiAPI.getBoxStock().then((r) => onSnapshot(r.data.data || [])).catch(() => {});
      }
    } catch (err) {
      setState({ status: 'failed', error: err.response?.data?.message || 'Failed to read serials' });
    }
  }, [onSnapshot]);

  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, [poll]);

  const start = async () => {
    try {
      const { data } = await gtplSaathiAPI.startBoxSerials();
      setState(data.data);
      timer.current = setTimeout(poll, 4000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start');
    }
  };

  const running = state.status === 'running';
  const boxes = state.boxes || [];
  return (
    <Panel icon={ListOrdered} tone="info" title="Spare box serial numbers"
      sub="From GTPL's STB INFO report (status BALANCE). Takes about a minute per entity that has spares."
      right={
        <button type="button" className="btn btn-sm btn-secondary" disabled={running} onClick={start}>
          <ListOrdered size={14} /> {running ? 'Reading GTPL…' : 'Show serials'}
        </button>}>
      {state.status === 'idle' && <Empty icon={ListOrdered} title="Not read yet">Press "Show serials".</Empty>}
      {running && <Notice tone="info">Reading GTPL Saathi — started {fmtDateTime(state.startedAt)}. You can leave this page; the result stays here.</Notice>}
      {state.status === 'failed' && <Notice tone="bad">{state.error}</Notice>}
      {state.status === 'done' && (
        boxes.length === 0 ? (
          <Empty tone="mute" title="No spare boxes on GTPL">Read {fmtDateTime(state.finishedAt)}.</Empty>
        ) : (
          <>
            <div className="gx-list">
              {boxes.map((b) => (
                <Item key={`${b.entityCode}-${b.serial}`}
                  title={<span style={{ fontFamily: 'monospace' }}>{b.serial}</span>} sub={b.model}
                  right={<Pill tone="info">{b.entityCode}</Pill>}
                  meta={b.quality ? <span>Quality: {b.quality}</span> : null} />
              ))}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '.75rem', marginTop: 8 }}>Read {fmtDateTime(state.finishedAt)}</div>
          </>
        )
      )}
    </Panel>
  );
}
